import type { SponsorRequestDto, SponsorRequestStatus } from '@ukinfo/shared';
import { SPONSOR_REQUEST_STATUS } from '@ukinfo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { EmptyState, Select, useToast } from '../ui';
import { REQUEST_STATUS_LABELS, RequestStatusBadge } from './common';

/**
 * Anfrageverlauf als Zeitleiste.
 * Eigene Anfragen (bzw. alle mit Verwalten-Recht) lassen sich weiterfuehren.
 */
export function RequestList({
  requests,
  showSponsor = true,
  emptyText = 'Noch keine Anfragen erfasst.',
}: {
  requests: SponsorRequestDto[];
  showSponsor?: boolean;
  emptyText?: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();

  const update = useMutation({
    mutationFn: (input: { id: string; status: SponsorRequestStatus }) =>
      api.patch(`/api/sponsor-requests/${input.id}`, { status: input.status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsor-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsors'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsoring-offers'] });
      toast.show('Anfrage aktualisiert');
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Ändern fehlgeschlagen', 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/sponsor-requests/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sponsor-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['sponsors'] });
      toast.show('Anfrage entfernt');
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Entfernen fehlgeschlagen', 'error'),
  });

  if (requests.length === 0) return <EmptyState title={emptyText} />;

  return (
    <ol className="relative space-y-3 border-l border-slate-200 pl-4">
      {requests.map((request) => (
        <li key={request.id} className="relative">
          <span aria-hidden className="absolute -left-[1.4rem] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-brand-500" />
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">
                {showSponsor && (
                  <>
                    <Link to={`/sponsoring/${request.sponsorId}`} className="text-brand-700 hover:underline">
                      {request.sponsorName}
                    </Link>
                    {' · '}
                  </>
                )}
                {request.topic}
              </p>
              <p className="text-xs text-slate-500">
                {[
                  request.requestedAt ? `angefragt ${formatDate(request.requestedAt)}` : `vorgemerkt ${formatDate(request.createdAt)}`,
                  request.requestedBy ? `von ${request.requestedBy.name}` : null,
                  request.offerTitle && request.offerTitle !== request.topic ? request.offerTitle : null,
                  request.organisationUnit?.name,
                ].filter(Boolean).join(' · ')}
              </p>
              {request.resultNote && <p className="mt-1 text-sm text-slate-600">{request.resultNote}</p>}
            </div>
            <div className="flex items-center gap-2">
              {request.canUpdate ? (
                <Select
                  aria-label={`Stand der Anfrage ${request.topic}`}
                  className="w-auto py-1 text-sm"
                  value={request.status}
                  disabled={update.isPending}
                  onChange={(e) => update.mutate({ id: request.id, status: e.target.value as SponsorRequestStatus })}
                >
                  {SPONSOR_REQUEST_STATUS.map((status) => (
                    <option key={status} value={status}>{REQUEST_STATUS_LABELS[status]}</option>
                  ))}
                </Select>
              ) : (
                <RequestStatusBadge status={request.status} />
              )}
              {request.canUpdate && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-rose-600"
                  onClick={() => {
                    if (window.confirm(`Anfrage „${request.topic}“ entfernen?`)) remove.mutate(request.id);
                  }}
                >
                  entfernen
                </button>
              )}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
